/**
 * Profile — your own.
 *
 * Name and About are editable in place; the phone number is not, because
 * it is the identity the account is keyed on. The server refuses to change
 * it too, so this is the interface agreeing with the rule rather than
 * enforcing it.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { userMessage, formatDisplay } from '@zivapp/shared'
import { useAuth } from '../context/index.js'
import Avatar from '../components/ui/Avatar.jsx'
import './Profile.css'

const LIMITS = { displayName: 25, about: 139 }

/**
 * One editable row.
 *
 * Defined at module scope, NOT inside Profile. A component declared inside
 * another is a new type on every render, so React unmounts and remounts it
 * — which for a text input means losing focus after each keystroke. It was
 * written that way first and typing a name was unusable.
 */
function Field ({ field, label, hint, value, editing, draft, saving, limit, onBegin, onDraft, onSave, onCancel }) {
  return (
    <section className="pf__field">
      <h2>{label}</h2>
      {editing ? (
        <div className="pf__edit">
          <input
            value={draft}
            onChange={e => onDraft(e.target.value.slice(0, limit))}
            onKeyDown={e => {
              if (e.key === 'Enter') onSave()
              if (e.key === 'Escape') onCancel()
            }}
            aria-label={label}
            autoFocus
          />
          <span className="pf__count">{limit - draft.length}</span>
          <button type="button" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      ) : (
        <button className="pf__value" type="button" onClick={() => onBegin(field)}>
          <span>{value || <em>Not set</em>}</span>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 20h4L19.4 8.6a2.4 2.4 0 0 0-3.4-3.4L4.6 16.6 4 20Z"
              stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      {hint && <p className="pf__hint">{hint}</p>}
    </section>
  )
}

export default function Profile () {
  const navigate = useNavigate()
  const { user, updateProfile } = useAuth()

  const [editing, setEditing] = useState(null)   // 'displayName' | 'about' | null
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  if (!user) return <p className="pf__state">Not signed in.</p>

  const begin = field => {
    setDraft(user[field] ?? '')
    setEditing(field)
    setError(null)
  }

  async function save () {
    const value = draft.trim()
    if (!value) return setEditing(null)
    setSaving(true)
    setError(null)
    try {
      await updateProfile({ [editing]: value })
      setEditing(null)
    } catch (err) {
      setError(userMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="pf">
      <header className="pf__bar">
        <button className="pf__back" type="button" onClick={() => navigate(-1)} aria-label="Back">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1>Profile</h1>
      </header>

      <div className="pf__scroll">
        <div className="pf__avatar">
          <Avatar name={user.displayName} url={user.avatarUrl} size={130} />
          <button className="pf__camera" type="button" aria-label="Change profile photo">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4 8h2.8l1.4-2.2h7.6L17.2 8H20a1.8 1.8 0 0 1 1.8 1.8v8.4A1.8 1.8 0 0 1 20 20H4a1.8 1.8 0 0 1-1.8-1.8V9.8A1.8 1.8 0 0 1 4 8Z"
                stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <circle cx="12" cy="13.8" r="3.4" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </button>
        </div>

        {error && <p className="pf__error" role="alert">{error}</p>}

        <Field
          field="displayName"
          label="Name"
          value={user.displayName}
          hint="This is not your username or a PIN. This name is visible to your ZIvapp contacts."
          limit={LIMITS.displayName}
          editing={editing === 'displayName'}
          draft={draft} saving={saving}
          onBegin={begin} onDraft={setDraft} onSave={save} onCancel={() => setEditing(null)}
        />

        <Field
          field="about"
          label="About"
          value={user.about}
          limit={LIMITS.about}
          editing={editing === 'about'}
          draft={draft} saving={saving}
          onBegin={begin} onDraft={setDraft} onSave={save} onCancel={() => setEditing(null)}
        />

        <section className="pf__field">
          <h2>Phone</h2>
          {/* Read-only on purpose: the number IS the account. The server
              rejects an attempt to change it, and so does this. */}
          <p className="pf__readonly">{formatDisplay(user.phone)}</p>
          <p className="pf__hint">
            Your number identifies your account and cannot be changed here.
          </p>
        </section>
      </div>
    </div>
  )
}
